import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import * as XLSX from 'xlsx';
import GLSyncMappingStep from './GLSyncMappingStep';
import { formatDate, formatAmount, initializeFormats, getFormatConfig } from '../../utils/global';
import { AuditLoggers } from '../../utils/auditLogger';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
} from '../../theme/harmonateStyles';
import {
  GL_SYNC_TRIAL_BALANCE_DOC_TYPE,
  ALLVUE_NON_ALLOCATED_MEMO_VIEW,
  isYardiAllvuePocDocumentType,
  toGlSyncViewConfigCategory,
} from '../../constants/glSyncDocumentTypes';

const fieldsFromRows = (rows) => {
  const first = Array.isArray(rows) && rows.length ? rows[0] : null;
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).map((key) => ({
    title: String(key).replace(/_/g, ' '),
    name: key,
    DataType: '',
  }));
};

const toLowercaseRow = (item) => {
  const transformed = {};
  Object.keys(item || {}).forEach((key) => {
    transformed[key.toLowerCase()] = item[key];
  });
  return transformed;
};
const toWorkflowStepViewName = (viewName) => {
  if (!viewName || typeof viewName !== 'string') return '';
  const t = viewName.trim();
  if (!t) return '';
  if (t.toUpperCase().startsWith('WORKFLOW.')) return t;
  return `WORKFLOW.${t}`;
};

/** Match Output step: use config.category for workflow/step (top-level or inside view_json_default). */
const extractCategoryFromConfigResponse = (raw) => {
  if (!raw || typeof raw !== 'object') return '';
  if (typeof raw.category === 'string' && raw.category.trim()) {
    return raw.category.trim();
  }
  let parsed = raw;
  if (typeof raw.view_json_default === 'string') {
    try {
      parsed = JSON.parse(raw.view_json_default);
    } catch {
      return '';
    }
  }
  if (typeof parsed?.category === 'string' && parsed.category.trim()) {
    return parsed.category.trim();
  }
  return '';
};

const extractGridFieldsFromConfig = (raw) => {
  if (!raw || typeof raw !== 'object') return [];
  let parsed = raw;
  if (typeof raw.view_json_default === 'string') {
    try {
      parsed = JSON.parse(raw.view_json_default);
    } catch {
      return [];
    }
  }
  const arr =
    parsed.grid?.columns ||
    parsed.comparison?.field ||
    parsed.output?.field ||
    parsed.data?.comparison?.field;
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((f) => f && f.visible !== false)
    .map((f) => ({
      title: f.title ?? f.name,
      name: f.name,
      DataType: f.DataType,
      width: f.width,
    }));
};

const extractSkipRowsFromConfig = (raw) => {
  if (!raw || typeof raw !== 'object') return 0;
  let parsed = raw;
  if (typeof raw.view_json_default === 'string') {
    try {
      parsed = JSON.parse(raw.view_json_default);
    } catch {
      return 0;
    }
  }
  const n = parsed?.output?.skip_data_rows ?? parsed?.grid?.skip_data_rows ?? 0;
  const count = Number(n);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

/** `conductor/config/default` `field.width` (comparison + output) e.g. "150px" — scrollable wide grids */
const parseOutputFieldWidthPx = (width) => {
  if (width == null || width === '') return 220;
  if (typeof width === 'number' && Number.isFinite(width)) return Math.max(120, width);
  const m = String(width).trim().match(/^(\d+(?:\.\d+)?)/);
  if (m) return Math.max(120, parseFloat(m[1], 10));
  return 220;
};

const isWideTextField = (field) => {
  const hay = `${field?.title || ''} ${field?.name || ''}`.toLowerCase();
  return /mapping|account|validation|content|description|file.?name|fund.?name|pre gl|post gl/.test(
    hay
  );
};

/**
 * Config widths are often ~120–150px (too tight). Bump them for Comparison/Output
 * readability while keeping horizontal scroll for many columns.
 */
const resolveGridColumnWidthPx = (field) => {
  const parsed = parseOutputFieldWidthPx(field?.width);
  const bumped = Math.round(parsed * 1.5);
  if (/validation/i.test(`${field?.title || ''} ${field?.name || ''}`)) {
    return Math.max(bumped, 340);
  }
  if (isWideTextField(field)) {
    return Math.max(bumped, 280);
  }
  return Math.max(bumped, 180);
};

const isFileNameField = (field) => {
  const title = String(field?.title || '').toLowerCase();
  const name = String(field?.name || '').toLowerCase();
  return /file[_\s-]?name/.test(title) || /file[_\s-]?name/.test(name);
};

/** Long S3 paths → short basename for the grid; full path stays in tooltip. */
const shortenPathForDisplay = (value) => {
  const s = String(value ?? '').trim();
  if (!s || s === '-') return s;
  const normalized = s.replace(/\\/g, '/');
  if (!normalized.includes('/')) return s;
  const base = normalized.split('/').filter(Boolean).pop();
  return base || s;
};

const gridCellClampSx = (colW) => ({
  minWidth: colW,
  width: colW,
  maxWidth: colW,
  py: 1,
  px: 1.5,
  fontSize: '13px',
  borderColor: ht.line,
  overflow: 'hidden',
  verticalAlign: 'middle',
});

const renderClampedCellContent = (display, fullTitle) => {
  const text = display == null || display === '' ? '' : String(display);
  const tip = fullTitle != null && String(fullTitle).trim() !== '' ? String(fullTitle) : text;
  return (
    <Tooltip title={tip === '' ? '' : tip} enterDelay={400} placement="top-start">
      <Box
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

/** Same loading layout for Comparison and Output (Mapping step uses the same pattern in GLSyncMappingStep). */
const TRIAL_BALANCE_STEP_LOADING_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  width: '100%',
  height: '100%',
  minHeight: 240,
  py: 4,
};

const renderTrialBalanceStepLoading = (message) => (
  <Box sx={TRIAL_BALANCE_STEP_LOADING_SX}>
    <CircularProgress size={32} />
    <Typography variant="body1" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const SyncTrialBalanceCompare = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sourceGL: navSourceGL,
    destinationGL,
    activeStep: initialStep = 0,
    workflowId,
    documentType: navDocumentType,
    uploadData,
    fundId: navFundId = null,
    fundCode: navFundCode = null,
    fundName: navFundName = null,
  } = location.state || {};
  const [sourceGL, setSourceGL] = useState(navSourceGL ?? null);
  const fundId = navFundId || uploadData?.fundId || null;
  const fundCode = navFundCode || uploadData?.fundCode || null;
  const fundName = navFundName || uploadData?.fundName || null;
  const documentTypeName =
    String(navDocumentType || uploadData?.documentType || '').trim() ||
    GL_SYNC_TRIAL_BALANCE_DOC_TYPE;
  const isAllvueOutput = isYardiAllvuePocDocumentType(documentTypeName);

  const [validationData, setValidationData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allvueOutputTab, setAllvueOutputTab] = useState('non_allocated');

  const [fieldConfig, setFieldConfig] = useState([]);
  const [, setSkipRows] = useState(0);
  // const steps = ['Comparison', 'Output', 'Mapping'];
  const steps = ['Mapping', 'Comparison', 'Output'];
  const [activeStep, setActiveStep] = useState(initialStep);
  const token = localStorage.getItem('token');

  const [amountFormat, setAmountFormat] = useState('#,###,###,##0.00');
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');

  /** Comparison grid: from conductor/config/default?category=<DOCUMENT_TYPE>_COMPARISON */
  const [validationFieldConfig, setValidationFieldConfig] = useState([]);
  /** workflow/step view_name from config.category (same as Output step). */
  const [validationWorkflowViewName, setValidationWorkflowViewName] = useState('');

  useEffect(() => {
    const s = location.state?.sourceGL;
    if (s !== undefined) setSourceGL(s ?? null);
  }, [location.state?.sourceGL]);

  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.AmountFormat) {
          setAmountFormat(config.AmountFormat);
        }
        if (config.DateFormat) {
          setDateFormat(config.DateFormat);
        }
      });
    }
  }, []);

  const formatCellValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return '';
    }

    // Convert to string and trim for comparison
    const strValue = String(value).trim();
    if (strValue === '') return '';

    // Get data type, defaulting to empty string if not found
    const dataType = (field.DataType || '').toLowerCase();

    try {
      // Format based on data type
      if (dataType === 'date' || field.name === 'COL_05') {
        // For date fields (COL_05)
        // First try to parse the date in DD.MM.YYYY format
        if (strValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          const [day, month, year] = strValue.split('.');
          return formatDate(new Date(year, month - 1, day), dateFormat);
        }
        return formatDate(strValue, dateFormat);
      } else if (dataType === 'number' || field.name === 'COL_10' || field.name === 'COL_11') {
        // For numeric fields (COL_10 and COL_11)
        const numValue = parseFloat(strValue.replace(/[^0-9.-]+/g, ''));
        return !isNaN(numValue) ? formatAmount(numValue, amountFormat) : strValue;
      }
    } catch (error) {
      console.error('Error formatting value:', { field, value, error });
    }

    // Return the original value if no formatting was applied
    return strValue;
  };

  const getDataKey = (field) => {
    const fieldName = String(field.name || '').toLowerCase().replace(/\s+/g, '_');
    return fieldName;
  };

  const comparisonFieldColumns = useMemo(
    () => validationFieldConfig,
    [validationFieldConfig]
  );

  useEffect(() => {
    if (activeStep === 1) {
      fetchComparisonConfigByDocumentType();
    } else if (activeStep === 2) {
      if (isAllvueOutput) {
        fetchAllvueOutput();
      } else {
        fetchComparisonData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, workflowId, sourceGL, destinationGL, documentTypeName, allvueOutputTab, isAllvueOutput]);

  useEffect(() => {
    if (activeStep !== 1 || !workflowId || !validationWorkflowViewName) {
      return;
    }
    fetchValidationData(validationWorkflowViewName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, workflowId, validationWorkflowViewName]);

  /** Avoid Output showing stale rows or skipping the loading state when re-entering the step. */
  useEffect(() => {
    if (activeStep !== 2) {
      setFieldConfig([]);
      setComparisonData([]);
    }
  }, [activeStep]);

  const fetchValidationFieldConfig = async (configCategory) => {
    const authToken = localStorage.getItem('token');
    const category = typeof configCategory === 'string' ? configCategory.trim() : '';
    if (!authToken || !category) {
      setValidationFieldConfig([]);
      setValidationWorkflowViewName('');
      return false;
    }

    setValidationFieldConfig([]);
    setValidationWorkflowViewName('');
    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/config/default?category=${encodeURIComponent(category)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`Comparison config not found for category ${category} (${res.status})`);
      }

      let raw = await res.json().catch(() => null);
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw);
        } catch {
          raw = null;
        }
      }
      const fields = extractGridFieldsFromConfig(raw);
      if (!fields.length) {
        throw new Error(`Comparison config ${category} has no grid columns`);
      }

      const stepView = extractCategoryFromConfigResponse(raw);
      if (!stepView) {
        throw new Error(`Comparison config ${category} has no Snowflake view in category`);
      }

      setValidationFieldConfig(fields);
      setValidationWorkflowViewName(toWorkflowStepViewName(stepView));
      return true;
    } catch (e) {
      console.error('fetchValidationFieldConfig:', e);
      setValidationFieldConfig([]);
      setValidationWorkflowViewName('');
      setError(`Failed to load comparison setup: ${e.message}`);
      return false;
    }
  };

  /**
   * Comparison columns + Snowflake view come from conductor/config/default
   * category = {DOCUMENT_TYPE}_COMPARISON (e.g. TRIAL_BALANCE_TRANSACTIONS_COMPARISON).
   */
  const fetchComparisonConfigByDocumentType = async () => {
    const authToken = localStorage.getItem('token');

    setError(null);
    setValidationFieldConfig([]);
    setValidationWorkflowViewName('');

    if (!authToken) {
      return;
    }

    const configCategory = toGlSyncViewConfigCategory(documentTypeName, 'COMPARISON');
    if (!configCategory) {
      setError('Document type is required to load the Comparison config.');
      return;
    }

    await fetchValidationFieldConfig(configCategory);
  };

  const fetchValidationData = async (viewNameForStep) => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');

    if (!token) {
      setError('No authentication token found');
      setLoading(false);
      return;
    }

    if (!tenantId) {
      setError('No tenant ID found. Please ensure you are properly authenticated.');
      setLoading(false);
      return;
    }

    if (!workflowId) {
      setError('No workflow ID found. Please ensure the workflow is provided.');
      setLoading(false);
      return;
    }

    const viewName =
      typeof viewNameForStep === 'string' && viewNameForStep.trim()
        ? viewNameForStep.trim()
        : validationWorkflowViewName;
    if (!viewName) {
      setValidationData([]);
      setLoading(false);
      return;
    }

    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
      const stepUrl = new URL(`${baseUrl}/fastapi/v1/workflow/step`);
      stepUrl.searchParams.set('tenant_id', tenantId);
      stepUrl.searchParams.set('workflow_instance_id', String(workflowId));
      stepUrl.searchParams.set('view_name', viewName);

      const response = await fetch(stepUrl.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const responseData = await response.json().catch((e) => {
        console.error('Error parsing JSON:', e);
        throw new Error('Invalid JSON response from server');
      });

      const data = responseData.data || [];

      const transformedData = data.map((item) => {
        const transformed = {};
        Object.keys(item).forEach((key) => {
          transformed[key.toLowerCase()] = item[key];
        });
        return transformed;
      });

      setValidationData(transformedData);
    } catch (err) {
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
      });

      setValidationData([]);
      setError(`Failed to load validation data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllvueOutput = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');
    if (!token) {
      setError('No authentication token found');
      setLoading(false);
      return;
    }
    if (!tenantId) {
      setError('No tenant ID found. Please ensure you are properly authenticated.');
      setLoading(false);
      return;
    }
    if (!workflowId) {
      setError('No workflow ID found. Please ensure the workflow is provided.');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
      let rows = [];
      if (allvueOutputTab === 'allocated') {
        const spUrl = new URL(`${baseUrl}/fastapi/v1/gl-sync/allvue-allocated-transactions`);
        spUrl.searchParams.set('tenant_id', tenantId);
        spUrl.searchParams.set('process_id', String(workflowId));
        const response = await fetch(spUrl.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Allocated transactions failed (${response.status})`);
        }
        const responseData = await response.json();
        rows = Array.isArray(responseData.data) ? responseData.data : [];
      } else {
        const stepUrl = new URL(`${baseUrl}/fastapi/v1/workflow/step`);
        stepUrl.searchParams.set('tenant_id', tenantId);
        stepUrl.searchParams.set('workflow_instance_id', String(workflowId));
        stepUrl.searchParams.set('view_name', ALLVUE_NON_ALLOCATED_MEMO_VIEW);
        const response = await fetch(stepUrl.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Non-allocated memo view failed (${response.status})`);
        }
        const responseData = await response.json();
        rows = Array.isArray(responseData.data) ? responseData.data : [];
      }

      const transformed = rows.map(toLowercaseRow);
      setComparisonData(transformed);
      const fields = fieldsFromRows(transformed);
      // Allvue Output columns come from row keys (not config JSON); hide internal columns per tab.
      const hiddenByTab = {
        non_allocated: new Set(['process_id']),
        allocated: new Set(['page_number']),
      };
      const hidden = hiddenByTab[allvueOutputTab] || new Set();
      setFieldConfig(fields.filter((f) => !hidden.has(String(f.name || '').toLowerCase())));
      setSkipRows(0);
    } catch (err) {
      console.error('Error loading Allvue output:', err);
      setError(`Failed to load Allvue output: ${err.message}`);
      setComparisonData([]);
      setFieldConfig([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparisonData = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_integration');

    if (!token) {
      setError('No authentication token found');
      setLoading(false);
      return;
    }

    if (!tenantId) {
      setError('No tenant ID found. Please ensure you are properly authenticated.');
      setLoading(false);
      return;
    }

    if (!workflowId) {
      setError('No workflow ID found. Please ensure the workflow is provided.');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003/').replace(/\/+$/, '');
      const configCategory = toGlSyncViewConfigCategory(documentTypeName, 'OUTPUT');
      if (!configCategory) {
        setFieldConfig([]);
        setComparisonData([]);
        throw new Error('Document type is required to load the Output config.');
      }

      const configResponse = await fetch(
        `${baseUrl}/conductor/config/default?category=${encodeURIComponent(configCategory)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      if (!configResponse.ok) {
        throw new Error(
          `Output config not found for category ${configCategory} (${configResponse.status})`
        );
      }

      let configData = await configResponse.json().catch(() => null);
      if (typeof configData === 'string') {
        try {
          configData = JSON.parse(configData);
        } catch {
          configData = null;
        }
      }

      const fieldNames = extractGridFieldsFromConfig(configData);
      if (!fieldNames.length) {
        throw new Error(`Output config ${configCategory} has no grid columns`);
      }

      const stepView = extractCategoryFromConfigResponse(configData);
      if (!stepView) {
        throw new Error(`Output config ${configCategory} has no Snowflake view in category`);
      }
      const viewName = toWorkflowStepViewName(stepView);
      const skipRowsCount = extractSkipRowsFromConfig(configData);

      setFieldConfig(fieldNames);
      setSkipRows(skipRowsCount);

      const stepUrl = new URL(`${baseUrl}/fastapi/v1/workflow/step`);
      stepUrl.searchParams.set('tenant_id', tenantId);
      stepUrl.searchParams.set('workflow_instance_id', String(workflowId));
      stepUrl.searchParams.set('view_name', viewName);

      const response = await fetch(stepUrl.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const responseData = await response.json();
      const data = responseData.data || [];

      const transformedData = data.map((item) => {
        const transformed = {};
        Object.keys(item).forEach((key) => {
          transformed[key.toLowerCase()] = item[key];
        });
        return transformed;
      });

      setComparisonData(
        transformedData.length === 0 ? [] : transformedData.slice(skipRowsCount)
      );
    } catch (err) {
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
      });

      setFieldConfig([]);
      setComparisonData([]);
      setError(`Failed to load output data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStepClick = async (stepIndex) => {
    const stepName = steps[stepIndex];
    setActiveStep(stepIndex);
    
    try {
      // Log when user views Comparison or Output tabs
      if (stepName === 'Comparison' || stepName === 'Output') {
        await AuditLoggers.custom(
          `User viewed ${stepName} for Trial Balance Compare`,
          `Trial Balance ${stepName} Viewed`,
          'handleStepClick',
          {
            sourceGL,
            destinationGL,
            workflowId,
            viewType: stepName.toLowerCase()
          }
        );
      }
    } catch (error) {
      console.error('Error logging view change:', error);
      // Don't throw error to prevent breaking the UI
    }
  };

  // const handleBackFromMapping = () => {
  //   setActiveStep(0); // Go back to Comparison step
  // };
  const handleBackFromMapping = () => {
    setActiveStep(0); // Stay on Mapping step
  };

  // Excel export functions
  const exportToExcel = (data, filename, sheetName, { skipHeader = false } = {}) => {
    try {
      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data, { skipHeader });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate Excel file and download
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export data to Excel');
    }
  };

  const handleDownloadComparison = async () => {
    if (validationData.length === 0) {
      setError('No comparison data available to export');
      return;
    }
    
    // Log the download action
    try {
      await AuditLoggers.glSyncDownloadComparison(
        sourceGL || 'Unknown',
        destinationGL || 'Unknown',
        {
          timestamp: new Date().toISOString(),
          userId: localStorage.getItem('user_id'),
          username: localStorage.getItem('username'),
          itemCount: validationData.length,
          workflowId: workflowId || 'N/A'
        }
      );
    } catch (logError) {
      console.error('Error logging comparison download:', logError);
      // Don't block the download if logging fails
    }

    const excelData = validationData.map((row) => {
      const mappedRow = {};
      comparisonFieldColumns.forEach((field) => {
        const dataKey = getDataKey(field);
        const cellValue = row[dataKey];
        const display =
          cellValue !== undefined && cellValue !== null
            ? formatCellValue({ ...field, DataType: field.DataType }, cellValue)
            : '';
        mappedRow[field.title] = display;
      });
      return mappedRow;
    });

    const filename = `GL_Comparison_${new Date().toISOString().split('T')[0]}.xlsx`;
    exportToExcel(excelData, filename, 'Comparison Data');
  };

  const handleDownloadOutput = async () => {
    if (comparisonData.length === 0) {
      setError('No output data available to export');
      return;
    }
    
    // Log the download action
    try {
      await AuditLoggers.glSyncDownloadOutput(
        sourceGL || 'Unknown',
        destinationGL || 'Unknown',
        {
          timestamp: new Date().toISOString(),
          userId: localStorage.getItem('user_id'),
          username: localStorage.getItem('username'),
          itemCount: comparisonData.length,
          workflowId: workflowId || 'N/A'
        }
      );
    } catch (logError) {
      console.error('Error logging output download:', logError);
      // Don't block the download if logging fails
    }

    // Create Excel data using the same field mapping as the grid
    const excelData = comparisonData.map(row => {
      const mappedRow = {};
      fieldConfig.forEach(field => {
        const dataKey = getDataKey(field);
        const cellValue = row[dataKey];
        // Use field.title as the Excel header (same as grid display)
        mappedRow[field.title] = cellValue !== undefined && cellValue !== null ? cellValue : '';
      });
      return mappedRow;
    });

    const filename = `GL_Output_${new Date().toISOString().split('T')[0]}.xlsx`;
    // Allocated SP export is label/value layout — skip col_* header row; Non-Allocated needs headers.
    exportToExcel(excelData, filename, 'Output Data', {
      skipHeader: isAllvueOutput && allvueOutputTab === 'allocated',
    });
  };



  const renderComparison = () => {
    const awaitingConfig = comparisonFieldColumns.length === 0;
    const isComparisonGridLoading = !error && (awaitingConfig || loading);

    return (
    <Paper elevation={0} sx={{
      ...harmonateCardSx,
      bgcolor: ht.white,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0, width: '100%' }}>
        {isComparisonGridLoading ? (
          renderTrialBalanceStepLoading('Loading validation data...')
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            <ErrorIcon sx={{ mb: 1, fontSize: 40 }} />
            <Typography>{error}</Typography>
          </Box>
        ) : (
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              minWidth: comparisonFieldColumns.reduce(
                (sum, f) => sum + resolveGridColumnWidthPx(f),
                0
              ),
            }}
          >
            <TableHead>
              <TableRow>
                {comparisonFieldColumns.map((field, index) => {
                  const colW = resolveGridColumnWidthPx(field);
                  return (
                    <TableCell
                      key={`${field.name}-${index}`}
                      sx={{
                        fontWeight: 600,
                        fontSize: '13px',
                        color: ht.text,
                        backgroundColor: ht.listBg,
                        minWidth: colW,
                        width: colW,
                        maxWidth: colW,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        verticalAlign: 'bottom',
                        py: 1.25,
                        px: 1,
                        borderColor: ht.line,
                        overflow: 'hidden',
                      }}
                    >
                      {field.title}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {validationData.length === 0 ? (
                <TableRow sx={{ '& > .MuiTableCell-root': { borderBottom: 'none' } }}>
                  <TableCell
                    colSpan={comparisonFieldColumns.length || 1}
                    sx={{
                      py: 3,
                      textAlign: 'center',
                      color: 'text.secondary',
                      borderBottom: 'none',
                    }}
                  >
                    No validation data available
                  </TableCell>
                </TableRow>
              ) : (
                validationData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': { backgroundColor: ht.listBg },
                      borderBottom: `1px solid ${ht.line}`,
                    }}
                  >
                    {comparisonFieldColumns.map((field, colIndex) => {
                      const dataKey = getDataKey(field);
                      const cellValue = row[dataKey];
                      const isValidationCol = String(field.name || '')
                        .toUpperCase()
                        .includes('VALIDATION');
                      const isNum = (field.DataType || '').toLowerCase() === 'number';
                      const formatted = formatCellValue(field, cellValue);
                      const display = isFileNameField(field)
                        ? shortenPathForDisplay(formatted)
                        : formatted;
                      const colW = resolveGridColumnWidthPx(field);
                      return (
                        <TableCell
                          key={`${field.name}-${colIndex}`}
                          sx={{
                            ...gridCellClampSx(colW),
                            color: isValidationCol ? ht.red : ht.text,
                            textAlign: isNum ? 'right' : 'left',
                          }}
                        >
                          {renderClampedCellContent(display, formatted)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!isComparisonGridLoading && !error ? (
        <Box
          component="footer"
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${ht.line}`,
            px: 2,
            py: 1,
            bgcolor: ht.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {validationData.length === 1
              ? '1 record'
              : `${validationData.length} records`}
          </Typography>
        </Box>
      ) : null}
    </Paper>
    );
  };

  const renderOutput = () => {
    const hasOutputContext = Boolean(workflowId) && !error;
    const isOutputGridLoading =
      hasOutputContext && (loading || (!isAllvueOutput && fieldConfig.length === 0));

    return (
      <Paper elevation={0} sx={{
        ...harmonateCardSx,
        bgcolor: ht.white,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {isAllvueOutput ? (
          <Tabs
            value={allvueOutputTab}
            onChange={(_event, value) => setAllvueOutputTab(value)}
            sx={{
              px: 2,
              minHeight: 42,
              borderBottom: `1px solid ${ht.line}`,
              '& .MuiTab-root': { textTransform: 'none', minHeight: 42, fontWeight: 600 },
            }}
          >
            <Tab value="non_allocated" label="Non-Allocated GL Entries" />
            <Tab value="allocated" label="Allocated Transactions" />
          </Tabs>
        ) : null}
        {isOutputGridLoading ? (
          <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {renderTrialBalanceStepLoading('Loading output data...')}
          </TableContainer>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <ErrorIcon sx={{ mb: 1, fontSize: 40, alignSelf: 'center' }} />
            <Typography>{error}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                width: '100%',
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  tableLayout: 'fixed',
                  minWidth: fieldConfig.reduce(
                    (sum, f) => sum + resolveGridColumnWidthPx(f),
                    0
                  ),
                }}
              >
                <TableHead>
                  <TableRow>
                    {fieldConfig.map((field, index) => {
                      const colW = resolveGridColumnWidthPx(field);
                      return (
                        <TableCell
                          key={`${field.name}-${index}`}
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            color: ht.text,
                            backgroundColor: ht.listBg,
                            borderBottom: `1px solid ${ht.line}`,
                            minWidth: colW,
                            width: colW,
                            maxWidth: colW,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            verticalAlign: 'bottom',
                            py: 1.25,
                            px: 1,
                            overflow: 'hidden',
                          }}
                        >
                          {field.title}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonData.length === 0 ? (
                    <TableRow sx={{ '& > .MuiTableCell-root': { borderBottom: 'none' } }}>
                      <TableCell
                        colSpan={Math.max(fieldConfig.length, 1)}
                        align="center"
                        sx={{ py: 4, color: 'text.secondary', border: 'none', borderBottom: 'none' }}
                      >
                        <Typography variant="body2">No output data available</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    comparisonData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { backgroundColor: ht.listBg },
                          borderBottom: `1px solid ${ht.line}`,
                        }}
                      >
                        {fieldConfig.map((field, colIndex) => {
                          const dataKey = getDataKey(field);
                          const cellValue = row[dataKey];
                          const colW = resolveGridColumnWidthPx(field);
                          const fieldWithType = {
                            ...field,
                            DataType:
                              field.DataType ||
                              (field.name === 'COL_05'
                                ? 'Date'
                                : field.name === 'COL_10' || field.name === 'COL_11'
                                  ? 'Number'
                                  : ''),
                          };
                          const formatted = formatCellValue(fieldWithType, cellValue);
                          const display = isFileNameField(field)
                            ? shortenPathForDisplay(formatted)
                            : formatted;

                          return (
                            <TableCell
                              key={`${field.name}-${colIndex}`}
                              align="center"
                              sx={gridCellClampSx(colW)}
                            >
                              {renderClampedCellContent(display, formatted)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              component="footer"
              sx={{
                flexShrink: 0,
                borderTop: `1px solid ${ht.line}`,
                px: 2,
                py: 1,
                bgcolor: ht.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {comparisonData.length === 1
                  ? '1 record'
                  : `${comparisonData.length} records`}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    );
  };


  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <GLSyncMappingStep
            selectedFile={location.state}
            onBackToStepper={handleBackFromMapping}
            sourceGL={sourceGL}
            destinationGL={destinationGL}
            fundId={fundId}
            fundCode={fundCode}
            fundName={fundName}
          />
        );
      case 1:
        return renderComparison();
      case 2:
        return renderOutput();
      default:
        return (
          <GLSyncMappingStep
            selectedFile={location.state}
            onBackToStepper={handleBackFromMapping}
            sourceGL={sourceGL}
            destinationGL={destinationGL}
            fundId={fundId}
            fundCode={fundCode}
            fundName={fundName}
          />
        );
    }
  };

  return (
    <Box sx={harmonatePageSx}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ fontSize: '0.9rem', '& .MuiBreadcrumbs-li': { color: ht.white }, '& a': { color: ht.white } }}
          separator={<NavigateNextIcon fontSize="small" sx={{ color: ht.white }} />}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/gl-sync')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, color: ht.white }}
          >
            <HomeIcon sx={{ fontSize: '1rem' }} />
            GL Sync
          </Link>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ht.white }}>
            Trial Balance Compare
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 2 }}>
        {/* <Typography variant="h5" sx={{
          fontWeight: 600,
          color: '#1f2937',
          fontSize: '20px'
        }}>
          {activeStep === 0 ? `${sourceGL} to ${destinationGL}` :
            activeStep === 1 ? `${sourceGL} to ${destinationGL}` :
              'Output'}
        </Typography> */}
      </Box>
      <Stepper
        activeStep={activeStep}
        sx={{
          mb: 3,
          '& .MuiStepLabel-label': {
            fontSize: '0.875rem',
            color: ht.white,
            '&.Mui-active, &.Mui-completed': { fontWeight: 600, color: ht.white }
          },
          '& .MuiStepIcon-root.Mui-active': { color: ht.blue },
          '& .MuiStepIcon-root.Mui-completed': { color: ht.green },
          '& .MuiStepConnector-line': { marginLeft: 1, marginRight: 1, borderColor: 'rgba(255,255,255,0.45)' }
        }}
      >
        {steps.map((label, index) => (
          <Step
            key={label}
            onClick={() => handleStepClick(index)}
            sx={{ cursor: 'pointer', '& .MuiStepLabel-root': { padding: '0 8px' } }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {renderStepContent()}
      </Box>
      {/* Download buttons for both steps */}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1 }}>
        {activeStep === 1 && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadComparison}
            sx={harmonateProceedButtonSx}
          >
            Download Comparison
          </Button>
        )}
        {activeStep === 2 && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadOutput}
            sx={harmonateProceedButtonSx}
          >
            Download Output
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SyncTrialBalanceCompare;
